import * as React from 'react';

import type * as analytics from '@gitbook/app-analytics/client';
import type { CustomHostnamePointer, HostnamePointerSite } from '@gitbook/app-database';
import { TextInput } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { Alert, Button, LoadingIndicator } from '@gitbook/spine-react';
import { type HTTPError, HTTPStatusCode, Logger, reportException } from '@gitbook/util-debug';
import { useLoadingState } from '@gitbook/util-react-hooks';

import { useSetAnalyticsScreen } from '../../navigation';
import { apiClient } from '../../state';
import { createDocumentationURL } from '../../utils';
import { DocumentationLink } from '../Link';
import { useCNAMERecord } from './useCNAMERecord';

const logger = Logger('app:custom-domain-setup');

interface ConfigureSubdomainStepProps {
    /** Custom Hostname user selected. */
    hostname: string;

    /** Callback when custom hostname was configured for a pointer. */
    onConfigure: () => void;

    /** Pointer to which to configure the domain. */
    pointer: CustomHostnamePointer | HostnamePointerSite;

    /** Indicates if user restarted the flow and used to toggle UI with additional info. */
    processRestarted: boolean;
}

/**
 * Step 2: Prompt to review the configuration connect the domain.
 */
export function ConfigureSubdomain(
    props: ConfigureSubdomainStepProps & { cname: string }
): React.ReactElement {
    const { pointer, hostname, processRestarted, onConfigure, cname } = props;
    const [loading, wrapLoading] = useLoadingState(false);
    const [setupHostnameError, setSetupHostnameError] = React.useState<string | null>(null);
    const subdomain = hostname.split('.')[0];

    const onConfirm = async () => {
        await wrapLoading(async () => {
            try {
                await apiClient.customHostnames.setupCustomHostname(hostname, { target: pointer });
            } catch (error) {
                if ((error as HTTPError).statusCode === HTTPStatusCode.Conflict) {
                    setSetupHostnameError((error as HTTPError).message);
                } else {
                    reportException(error);
                    setSetupHostnameError(
                        'An unexpected error occurred, please try again or contact our support.'
                    );
                    logger.error('Unexpected error: ', error);
                }
                return;
            }

            onConfigure();
        });
    };

    return (
        <div className="mx-auto flex flex-col items-center px-6">
            <div className="mt-4 flex w-full flex-col items-center gap-4 px-1 py-10">
                <div className="text-center text-ui-heading-large">Configure your DNS</div>
                <p className="text-muted">
                    Add a CNAME record to your domain by visiting your DNS provider or registrar.
                </p>
            </div>
            <div className="mb-6 flex w-full gap-4 rounded bg-muted p-4">
                <div className="flex flex-col gap-2">
                    <div className="text-muted text-ui-small">Type</div>
                    <Button
                        label="CNAME"
                        trailingIcon={Icon.ChevronDown}
                        kind="secondary"
                        disabled
                        className="h-[2.25rem]"
                    />
                </div>
                <div className="flex flex-col gap-2">
                    <div className="text-muted text-ui-small">Name</div>
                    <div className="flex flex-1">
                        <TextInput value={subdomain} copyable readOnly />
                    </div>
                </div>
                <div className="flex flex-col gap-2">
                    <div className="text-muted text-ui-small">Target</div>
                    <TextInput value={cname} copyable readOnly />
                </div>
            </div>
            {setupHostnameError ? (
                <div className="flex w-full flex-col">
                    <Alert
                        kind="danger"
                        details={setupHostnameError}
                        action={{
                            label: 'More info',
                            href: createDocumentationURL(
                                'publishing/custom-domain/troubleshooting#domain-already-connected-error-your-subdomain-is-already-configured-for-different-content.'
                            ),
                            target: '_blank',
                        }}
                        className="mb-6"
                    />
                </div>
            ) : null}
            {processRestarted ? (
                <div className="mb-10 flex items-center justify-center gap-2">
                    <Icon.InfoCircle size={16} className="text-primary" />
                    <p className="text-muted text-ui-small">
                        Having trouble? You might need to use {subdomain}.{cname} instead.
                    </p>
                </div>
            ) : undefined}
            <Button
                label="Next"
                kind="primary"
                onPress={onConfirm}
                disabled={loading || setupHostnameError !== null}
                leadingIcon={loading ? LoadingIndicator : undefined}
                className="mt-6 mb-4 w-full max-w-[16rem] self-center"
            />
            <div className="my-1 flex items-center gap-2 self-center text-primary">
                <Icon.HelpCircle size={16} className="m-nudge-icon" />
                <DocumentationLink path="publishing/custom-domain/configure-dns">
                    Read our setup guide
                </DocumentationLink>
            </div>
        </div>
    );
}

export function ConfigureSubdomainStep(props: ConfigureSubdomainStepProps): React.ReactElement {
    const cname = useCNAMERecord(props.pointer);

    return <ConfigureSubdomain cname={cname} {...props} />;
}

export function SiteConfigureSubdomainStep(
    props: ConfigureSubdomainStepProps & {
        /** Context for analytics events */
        analyticsContext: analytics.AnalyticsEventContext;
        cname: string;
    }
): React.ReactElement {
    const { analyticsContext, ...rest } = props;
    useSetAnalyticsScreen(
        {
            ...analyticsContext,
            screen: 'site_custom_domain',
            screenTab: 'configure',
        },
        [analyticsContext]
    );

    return <ConfigureSubdomain {...rest} />;
}
